import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Heart, CheckCircle, Loader } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DonationSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [transaction, setTransaction] = useState(null);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setTransaction(response.data);
          setStatus('success');
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setStatus('success'); // Assume success after timeout
        }
      } catch (error) {
        setStatus('error');
      }
    };
    
    poll();
  };

  return (
    <Layout>
      <section className="py-16 min-h-screen flex items-center justify-center" data-testid="donation-success">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="cassette-card p-12 text-center">
            {status === 'checking' && (
              <>
                <Loader className="w-16 h-16 mx-auto mb-6 text-[#6B4423] animate-spin" />
                <h1 className="text-3xl retro-title text-[#3D2817] mb-4">Processing...</h1>
                <p className="text-[#6B4423]">Verifying your donation</p>
              </>
            )}
            
            {status === 'success' && (
              <>
                <Heart className="w-20 h-20 mx-auto mb-6 text-[#6B4423]" fill="currentColor" />
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-700" />
                <h1 className="text-4xl retro-title text-[#3D2817] mb-4">Thank You!</h1>
                <p className="text-[#6B4423] text-lg mb-6">
                  Your generous donation helps us provide affordable, holistic healthcare to our community.
                </p>
                {transaction && (
                  <div className="bg-[#6B4423]/10 p-4 rounded mb-6">
                    <p className="text-[#3D2817] font-semibold">
                      Donation Amount: ${transaction.amount.toFixed(2)}
                    </p>
                    <p className="text-[#6B4423] text-sm mt-1">
                      A receipt has been sent to your email
                    </p>
                  </div>
                )}
                <Link to="/" className="retro-button inline-block" data-testid="return-home">
                  Return to Home
                </Link>
              </>
            )}
            
            {status === 'error' && (
              <>
                <h1 className="text-3xl retro-title text-[#3D2817] mb-4">Something Went Wrong</h1>
                <p className="text-[#6B4423] mb-6">
                  We couldn't verify your payment. Please check your email for confirmation.
                </p>
                <Link to="/" className="retro-button inline-block">
                  Return to Home
                </Link>
              </>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}